import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ShieldCheck, Buildings, CalendarBlank, ListChecks, MagnifyingGlass, ArrowRight, Warning, Clock, Briefcase } from "@phosphor-icons/react";
import { ScoreRing, StatusCounts, StatusBadge } from "../components/compliance/ComplianceBadges";
import { ComplianceCalendar } from "../components/compliance/ComplianceCalendar";
import { ComplianceTypes } from "../components/compliance/ComplianceTypes";
import { fmtD, daysText } from "../components/compliance/complianceUtils";

export default function BuildingCompliancePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const [tab, setTab] = useState("buildings");
  const [ov, setOv] = useState(null);
  const [types, setTypes] = useState(null);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all");

  const load = useCallback(() => {
    API.get("/compliance/overview").then((r) => setOv(r.data)).catch(() => setOv(null));
    API.get("/compliance/types").then((r) => setTypes(r.data)).catch(() => setTypes(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const s = ov?.summary;
  const props = (ov?.properties || []).filter((p) => {
    if (q && !`${p.name} ${p.client_name} ${p.address}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (only === "attention") return p.rollup.expired + p.rollup.expiring + p.rollup.missing > 0;
    if (only === "unconfigured") return !p.configured;
    return true;
  });

  return (
    <div className="space-y-5" data-testid="compliance-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold font-[Chivo] flex items-center gap-2"><ShieldCheck size={26} className="text-primary" /> Building compliance</h1>
          <p className="text-sm text-muted-foreground">Statutory certificates per property — expiry alerts, auto-raised renewal jobs and a one-click audit pack.</p>
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="compliance-stats">
          <Tile label="Portfolio score" value={s.score === null ? "—" : `${s.score}%`} tone={s.score === null ? "" : s.score >= 90 ? "text-emerald-600" : s.score >= 60 ? "text-amber-600" : "text-red-600"} testid="compliance-stat-score" />
          <Tile label="Buildings set up" value={`${s.configured_properties}/${s.properties}`} testid="compliance-stat-buildings" onClick={() => { setTab("buildings"); setOnly("unconfigured"); }} />
          <Tile label="Expired" value={s.expired} tone={s.expired ? "text-red-600" : ""} icon={Warning} testid="compliance-stat-expired" onClick={() => { setTab("buildings"); setOnly("attention"); }} />
          <Tile label="Expiring" value={s.expiring} tone={s.expiring ? "text-amber-600" : ""} icon={Clock} testid="compliance-stat-expiring" onClick={() => setTab("calendar")} />
          <Tile label="No certificate" value={s.missing} tone={s.missing ? "text-muted-foreground" : ""} testid="compliance-stat-missing" />
          <Tile label="Due in 60 days" value={s.due_60d} testid="compliance-stat-due60" onClick={() => setTab("calendar")} />
          <Tile label="Open renewal jobs" value={s.renewal_jobs_open} icon={Briefcase} testid="compliance-stat-jobs" onClick={() => navigate("/jobs")} />
        </div>
      )}

      {ov?.attention?.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="compliance-attention">
          <p className="text-xs font-semibold mb-2">Needs attention now</p>
          <div className="flex flex-wrap gap-2">
            {ov.attention.slice(0, 8).map((r) => (
              <Link key={r.id} to={`/compliance/properties/${r.property_id}`} className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-primary/50" data-testid={`attention-${r.id}`}>
                <StatusBadge status={r.status} small /><span className="font-medium">{r.type_short}</span><span className="text-muted-foreground">{r.property_name}</span><span className="text-muted-foreground">· {daysText(r)}</span>
              </Link>
            ))}
            {ov.attention.length > 8 && <span className="text-xs text-muted-foreground self-center">+{ov.attention.length - 8} more in the calendar</span>}
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="buildings" data-testid="compliance-tab-buildings"><Buildings size={14} className="mr-1" /> Buildings</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="compliance-tab-calendar"><CalendarBlank size={14} className="mr-1" /> Calendar</TabsTrigger>
          <TabsTrigger value="types" data-testid="compliance-tab-types"><ListChecks size={14} className="mr-1" /> Types &amp; intervals</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "buildings" && (
        <div className="space-y-3" data-testid="compliance-buildings">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search building, client, address…" className="pl-8 h-9" data-testid="compliance-search" />
            </div>
            <div className="flex gap-1">
              {[["all", "All"], ["attention", "Needs attention"], ["unconfigured", "Not set up"]].map(([k, l]) => (
                <button key={k} onClick={() => setOnly(k)} className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${only === k ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`} data-testid={`compliance-filter-${k}`}>{l}</button>
              ))}
            </div>
          </div>
          {!ov ? <div className="h-40 animate-pulse rounded-lg bg-muted/40" /> : props.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="compliance-empty">
              {ov.properties.length === 0 ? "No properties yet — add properties to your clients first, then set up their compliance here." : "No buildings match."}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
              {props.map((p) => (
                <button key={p.property_id} onClick={() => navigate(`/compliance/properties/${p.property_id}`)} className="text-left rounded-lg border border-border bg-card p-4 hover:border-primary/50 hover:shadow-sm transition-all group" data-testid={`compliance-building-${p.property_id}`}>
                  <div className="flex items-start gap-3">
                    <ScoreRing score={p.rollup.score} />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">{p.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{[p.client_name, p.address].filter(Boolean).join(" · ")}</p>
                      <div className="mt-2"><StatusCounts roll={p.rollup} /></div>
                      {p.rollup.next_expiry && <p className="text-[11px] text-muted-foreground mt-1.5">Next: {p.rollup.next_expiry_type} · {fmtD(p.rollup.next_expiry)}</p>}
                    </div>
                    <ArrowRight size={16} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                  </div>
                  {!p.configured && <p className="mt-2 text-xs text-primary font-medium">Set up requirements →</p>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "calendar" && <ComplianceCalendar categories={ov?.categories || []} />}
      {tab === "types" && (types ? <ComplianceTypes types={types.types} categories={types.categories} canEdit={canEdit} onChanged={load} /> : <div className="h-40 animate-pulse rounded-lg bg-muted/40" />)}
    </div>
  );
}

function Tile({ label, value, tone = "", icon: I, testid, onClick }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp onClick={onClick} className={`rounded-lg border border-border bg-card p-3 text-left ${onClick ? "hover:border-primary/50 transition-colors" : ""}`} data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">{I && <I size={12} />}{label}</p>
      <p className={`text-2xl font-bold mt-1 ${tone}`}>{value}</p>
    </Comp>
  );
}
